// Shared building blocks for the Grammar Lab hub and the core labs (क्रियारूप, विभक्ति-रूप, सर्वनाम):
// page header with the way back to the hub, section headings, toggles, chips, honest notices and the "more labs" strip.

import type { FC, KeyboardEvent, ReactNode } from 'react'

import Link from 'next/link'
import { ArrowLeft, ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { usePrefs } from '@/lib/client/prefs'
import { Switch } from '@/components/ui/switch'
import { Bi, Eyebrow } from '@/prakrit/components/common/bi'
import ReportIssueButton from '@/components/app/report-issue-button'
import { LABS } from '@/prakrit/components/grammar/labs/lab-catalog'

export interface LangText {
    hi: string
    en: string
}

export const CONTAINER = 'mx-auto w-full max-w-7xl px-4 sm:px-6'

export const CARD = 'rounded-xl border border-border bg-card p-5 shadow-card'

export const PRIMARY_BUTTON = 'inline-flex min-h-tap items-center justify-center gap-2 rounded-xl bg-primary px-5 text-sm font-semibold text-primary-foreground shadow-sm transition-colors hover:bg-primary/85 disabled:pointer-events-none disabled:opacity-50'

export const SECONDARY_BUTTON = 'inline-flex min-h-tap items-center justify-center gap-2 rounded-xl border border-border bg-card px-4 text-sm font-medium transition-colors hover:border-saffron/60 hover:bg-saffron/5 disabled:pointer-events-none disabled:opacity-50'

/** Kids mode, known only after the saved preferences are read (false on the server and on the first render). */
export const useKidsMode = (): boolean => {
    const { mode, hydrated } = usePrefs()

    return hydrated && mode === 'kids'
}

/** Devanagari digits for counts shown in Hindi (२ वचन, ८ विभक्तियाँ). */
export const hiDigits = (value: number): string => String(value).replace(/[0-9]/g, d => String.fromCharCode(0x0966 + Number(d)))

/** Move focus between the buttons of a vertical list with the arrow keys (Home / End jump to the ends). */
export const listKeyNav = (event: KeyboardEvent<HTMLElement>): void => {
    const keys = ['ArrowDown', 'ArrowUp', 'Home', 'End']

    if (!keys.includes(event.key)) return

    const items = Array.from(event.currentTarget.querySelectorAll<HTMLElement>('[data-nav-item]'))
    const index = items.indexOf(document.activeElement as HTMLElement)

    if (items.length === 0) return

    event.preventDefault()

    let next = index

    if (event.key === 'ArrowDown') next = Math.min(items.length - 1, index + 1)
    else if (event.key === 'ArrowUp') next = Math.max(0, index - 1)
    else if (event.key === 'Home') next = 0
    else next = items.length - 1

    items[next]?.focus()
}

export const BackToHub: FC = (): ReactNode => (
    <nav aria-label='Breadcrumb'>
        <Link
            href='/grammar'
            className='group inline-flex min-h-tap items-center gap-2 rounded-full border border-border bg-card/80 px-4 text-sm font-medium transition-colors hover:border-saffron/60 hover:text-prakrit'
        >
            <ArrowLeft className='size-4 transition-transform group-hover:-translate-x-0.5' aria-hidden='true' />
            <span lang='hi' className='hindi'>व्याकरण प्रयोगशाला</span>
            <span aria-hidden='true' className='text-muted-foreground'>·</span>
            <span lang='en'>Grammar Lab</span>
        </Link>
    </nav>
)

interface LabHeaderProps {
    eyebrow: string
    title: LangText
    intro: LangText
    /** A shorter, simpler introduction for Kids mode (shown with the kids-only class, so no hydration jump). */
    kidsIntro?: LangText
    /** Rule numbers in the course book, e.g. "नि.९–१४". */
    source?: string
    children?: ReactNode
}

export const LabHeader: FC<LabHeaderProps> = ({ eyebrow, title, intro, kidsIntro, source, children }): ReactNode => (
    <header className='flex flex-col gap-6 pb-8 pt-6 sm:pb-10 sm:pt-10'>
        <BackToHub />
        <div className='flex max-w-3xl flex-col gap-3'>
            <Eyebrow>{eyebrow}</Eyebrow>
            <h1 className='flex flex-col gap-1.5'>
                <span lang='hi' className='text-[2rem] font-bold leading-tight text-foreground sm:text-[2.5rem]'>{title.hi}</span>
                <span lang='en' className='font-sans text-lg font-medium leading-snug text-muted-foreground sm:text-xl'>{title.en}</span>
            </h1>
            <Bi hi={intro.hi} en={intro.en} className={cn('gap-1 text-base leading-relaxed sm:text-[1.0625rem]', kidsIntro && 'adults-only')} />
            {kidsIntro && <Bi hi={kidsIntro.hi} en={kidsIntro.en} className='kids-only gap-1 text-base leading-relaxed sm:text-[1.0625rem]' />}
            {source && (
                <p className='text-sm text-muted-foreground'>
                    <span lang='hi' className='hindi'>नियम: {source}, प्राकृत स्वयं-शिक्षक (प्रेम सुमन जैन)</span>
                    <span aria-hidden='true' className='ex-sep'> · </span>
                    <span lang='en' data-ex='en'>Rules from the course book</span>
                </p>
            )}
        </div>
        {children}
    </header>
)

interface SectionTitleProps {
    id?: string
    hi: string
    en: string
    level?: 'h2' | 'h3'
    className?: string
    children?: ReactNode
}

export const SectionTitle: FC<SectionTitleProps> = ({ id, hi, en, level = 'h2', className, children }): ReactNode => {
    const Tag = level

    return (
        <div className={cn('flex flex-col gap-1.5', className)}>
            <Tag id={id} className='flex scroll-mt-24 flex-wrap items-baseline gap-x-3 gap-y-0.5'>
                <span lang='hi' className={cn('font-bold text-foreground', level === 'h2' ? 'text-2xl sm:text-[1.75rem]' : 'text-lg sm:text-xl')}>{hi}</span>
                <span lang='en' className='font-sans text-base font-medium text-muted-foreground'>{en}</span>
            </Tag>
            {children}
        </div>
    )
}

/** A small caption above a control group: Hindi with the English beside it. */
export const ControlLabel: FC<{ hi: string, en: string, id?: string, className?: string }> = ({ hi, en, id, className }): ReactNode => (
    <p id={id} className={cn('text-sm font-semibold', className)}>
        <span lang='hi' className='hindi'>{hi}</span>
        <span aria-hidden='true' className='text-muted-foreground'> · </span>
        <span lang='en' className='font-medium text-muted-foreground'>{en}</span>
    </p>
)

interface SegmentedOption<T extends string> {
    value: T
    hi: string
    en: string
    icon?: ReactNode
    disabled?: boolean
}

interface SegmentedProps<T extends string> {
    label: string
    value: T
    options: SegmentedOption<T>[]
    onChange: (value: T) => void
    className?: string
}

/** Mutually exclusive buttons (aria-pressed), e.g. तालिका / छिपाकर याद करें. */
export function Segmented<T extends string>({ label, value, options, onChange, className }: SegmentedProps<T>): ReactNode {
    return (
        <div role='group' aria-label={label} className={cn('inline-flex flex-wrap gap-1 rounded-xl bg-secondary p-1', className)}>
            {options.map(o => (
                <button
                    key={o.value}
                    type='button'
                    aria-pressed={value === o.value}
                    disabled={o.disabled}
                    onClick={() => onChange(o.value)}
                    className={cn(
                        'inline-flex min-h-tap items-center gap-2 rounded-lg px-3.5 text-sm font-medium transition-colors disabled:pointer-events-none disabled:opacity-50',
                        value === o.value ? 'bg-card text-foreground shadow-sm ring-1 ring-border' : 'text-muted-foreground hover:text-foreground'
                    )}
                >
                    {o.icon}
                    <span lang='hi' className='hindi'>{o.hi}</span>
                    <span lang='en' className='text-xs text-muted-foreground'>{o.en}</span>
                </button>
            ))}
        </div>
    )
}

interface ChipProps {
    pressed: boolean
    onClick: () => void
    children: ReactNode
    className?: string
    label?: string
}

/** A toggle chip for filters and pickers. */
export const Chip: FC<ChipProps> = ({ pressed, onClick, children, className, label }): ReactNode => (
    <button
        type='button'
        aria-pressed={pressed}
        aria-label={label}
        onClick={onClick}
        className={cn(
            'inline-flex min-h-tap items-center gap-2 rounded-full border px-4 py-1.5 text-sm font-medium transition-colors',
            pressed ? 'border-prakrit/40 bg-prakrit/8 text-prakrit ring-1 ring-prakrit/20' : 'border-border bg-card hover:border-saffron/60 hover:bg-saffron/5',
            className
        )}
    >
        {children}
    </button>
)

interface ToggleRowProps {
    checked: boolean
    onChange: (checked: boolean) => void
    hi: string
    en: string
    className?: string
}

/** A labelled switch, e.g. विकल्प रूप दिखाएँ · Show variant forms. */
export const ToggleRow: FC<ToggleRowProps> = ({ checked, onChange, hi, en, className }): ReactNode => (
    <label className={cn('inline-flex min-h-tap cursor-pointer items-center gap-3 rounded-xl border border-border bg-card px-3.5 py-1.5', className)}>
        <Switch checked={checked} onCheckedChange={value => onChange(Boolean(value))} />
        <span className='flex flex-col leading-tight'>
            <span lang='hi' className='hindi text-sm font-medium'>{hi}</span>
            <span lang='en' className='text-xs text-muted-foreground'>{en}</span>
        </span>
    </label>
)

const NOTICE_TONE = {
    info: 'border-border bg-surface-muted/70',
    review: 'border-saffron/40 bg-saffron/8',
    link: 'border-terracotta/35 bg-terracotta/6',
    danger: 'border-danger/30 bg-danger-soft',
    success: 'border-success/30 bg-success-soft'
}

interface NoticeProps {
    tone?: keyof typeof NOTICE_TONE
    icon?: ReactNode
    children: ReactNode
    className?: string
}

export const Notice: FC<NoticeProps> = ({ tone = 'info', icon, children, className }): ReactNode => (
    <div className={cn('flex items-start gap-3 rounded-xl border px-4 py-3 text-sm', NOTICE_TONE[tone], className)}>
        {icon && <span className='mt-0.5 shrink-0' aria-hidden='true'>{icon}</span>}
        <div className='flex min-w-0 flex-1 flex-col gap-1'>{children}</div>
    </div>
)

/** Links to the other rooms of the Grammar Lab. */
const LabLinks: FC<{ current: string }> = ({ current }): ReactNode => (
    <nav aria-labelledby='more-labs' className='mt-16 border-t border-border pt-10'>
        <h2 id='more-labs' className='flex flex-wrap items-baseline gap-x-3'>
            <span lang='hi' className='text-xl font-bold'>और प्रयोगशालाएँ</span>
            <span lang='en' className='font-sans text-sm font-medium text-muted-foreground'>More in the Grammar Lab</span>
        </h2>
        <ul className='mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4'>
            {LABS.filter(l => l.href !== current).map(l => {
                const Icon = l.icon

                return (
                    <li key={l.href}>
                        <Link
                            href={l.href}
                            className='group flex min-h-tap items-center gap-3 rounded-xl border border-border bg-card px-4 py-3 transition-colors hover:border-saffron/60 hover:bg-saffron/5'
                        >
                            <Icon className='size-5 shrink-0 text-saffron-strong' aria-hidden='true' />
                            <span className='flex min-w-0 flex-1 flex-col leading-tight'>
                                <span lang='hi' className='hindi font-medium'>{l.eyebrow}</span>
                                <span lang='en' className='text-xs text-muted-foreground'>{l.en}</span>
                            </span>
                            <ArrowRight className='size-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5' aria-hidden='true' />
                        </Link>
                    </li>
                )
            })}
        </ul>
    </nav>
)

/** "Report a language issue" under a lab's content, then links to the other rooms of the Grammar Lab. */
export const LabNav: FC<{ current: string }> = ({ current }): ReactNode => {
    const room = LABS.find(l => l.href === current)

    return (
        <>
            <div className='mt-12'>
                <ReportIssueButton course='prakrit' itemLabel={room ? `${room.eyebrow} · ${room.en}` : undefined} className='-ml-3' />
            </div>
            <LabLinks current={current} />
        </>
    )
}
